/**
 * File Upload Widget
 * Drag-and-drop file upload supporting WORD, EXCEL, PPX, PDF, JPEG, TIFF
 * from local file systems, plus OneDrive and Google Drive cloud picker integration
 * via Microsoft Graph API and Google Picker API.
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  FileText,
  Cloud,
  Loader2,
  CheckCircle,
  AlertCircle,
  HardDrive,
} from 'lucide-react';

export interface UploadedFile {
  file?: File;
  name: string;
  size: number;
  type: string;
  source: 'local' | 'onedrive' | 'google_drive';
  cloudFileId?: string;
  cloudUrl?: string;
}

interface FileUploadWidgetProps {
  onFileSelected: (file: UploadedFile) => void;
  onCancel?: () => void;
  accept?: string;
  maxSizeMB?: number;
  showCloudPickers?: boolean;
}

const ACCEPTED_EXTENSIONS = [
  '.doc', '.docx',           // Word
  '.xls', '.xlsx',           // Excel
  '.ppt', '.pptx',           // PowerPoint
  '.pdf',                    // PDF
  '.jpg', '.jpeg',           // JPEG
  '.tif', '.tiff',           // TIFF
];

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/tiff',
];

const ACCEPT_STRING = ACCEPTED_MIME_TYPES.join(',') + ',' + ACCEPTED_EXTENSIONS.join(',');

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function isAcceptedFile(file: File): boolean {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_MIME_TYPES.includes(file.type);
}

export const FileUploadWidget: React.FC<FileUploadWidgetProps> = ({
  onFileSelected,
  onCancel,
  showCloudPickers = true,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cloudPickerLoading, setCloudPickerLoading] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFile = useCallback((file: File) => {
    setError(null);
    if (!isAcceptedFile(file)) {
      setError(`Unsupported file type. Accepted: Word, Excel, PowerPoint, PDF, JPEG, TIFF`);
      return;
    }
    const uploaded: UploadedFile = {
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      source: 'local',
    };
    setSelectedFile(uploaded);
    onFileSelected(uploaded);
  }, [onFileSelected]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [processFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  }, [processFile]);

  const handleOneDrivePicker = async () => {
    setCloudPickerLoading('onedrive');
    setError(null);
    try {
      // OneDrive Picker via Microsoft Graph API
      // This uses the OneDrive file picker SDK
      const pickerOptions = {
        clientId: '', // Set via admin OAuth config
        action: 'download',
        multiSelect: false,
        advanced: {
          filter: '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.jpg,.jpeg,.tif,.tiff',
        },
        success: (files: any) => {
          if (files?.value?.[0]) {
            const f = files.value[0];
            const uploaded: UploadedFile = {
              name: f.name,
              size: f.size || 0,
              type: f.name.split('.').pop() || '',
              source: 'onedrive',
              cloudFileId: f.id,
              cloudUrl: f['@microsoft.graph.downloadUrl'] || f.webUrl,
            };
            setSelectedFile(uploaded);
            onFileSelected(uploaded);
          }
          setCloudPickerLoading(null);
        },
        cancel: () => setCloudPickerLoading(null),
        error: (err: any) => {
          console.error('OneDrive picker error:', err);
          setError('Failed to open OneDrive picker. Ensure admin has configured Microsoft OAuth integration.');
          setCloudPickerLoading(null);
        },
      };

      // Check if OneDrive SDK is loaded
      if (typeof (window as any).OneDrive !== 'undefined') {
        (window as any).OneDrive.open(pickerOptions);
      } else {
        setError('OneDrive integration not configured. Admin must set up Microsoft OAuth in Settings.');
        setCloudPickerLoading(null);
      }
    } catch (err) {
      console.error('OneDrive picker error:', err);
      setError('Failed to open OneDrive picker.');
      setCloudPickerLoading(null);
    }
  };

  const handleGoogleDrivePicker = async () => {
    setCloudPickerLoading('google_drive');
    setError(null);
    try {
      // Google Picker API
      if (typeof (window as any).google !== 'undefined' && (window as any).google.picker) {
        const google = (window as any).google;
        const view = new google.picker.DocsView()
          .setMimeTypes(ACCEPTED_MIME_TYPES.join(','));

        const picker = new google.picker.PickerBuilder()
          .addView(view)
          .setCallback((data: any) => {
            if (data.action === google.picker.Action.PICKED) {
              const doc = data.docs[0];
              const uploaded: UploadedFile = {
                name: doc.name,
                size: doc.sizeBytes || 0,
                type: doc.mimeType || '',
                source: 'google_drive',
                cloudFileId: doc.id,
                cloudUrl: doc.url,
              };
              setSelectedFile(uploaded);
              onFileSelected(uploaded);
            }
            setCloudPickerLoading(null);
          })
          .build();
        picker.setVisible(true);
      } else {
        setError('Google Drive integration not configured. Admin must set up Google OAuth in Settings.');
        setCloudPickerLoading(null);
      }
    } catch (err) {
      console.error('Google Drive picker error:', err);
      setError('Failed to open Google Drive picker.');
      setCloudPickerLoading(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* Drag-and-Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : selectedFile
            ? 'border-green-300 bg-green-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_STRING}
          onChange={handleFileInput}
          className="hidden"
        />

        {selectedFile ? (
          <div className="flex items-center justify-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">
                {formatFileSize(selectedFile.size)}
                {selectedFile.source !== 'local' && (
                  <span className="ml-2 text-blue-600">
                    via {selectedFile.source === 'onedrive' ? 'OneDrive' : 'Google Drive'}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div>
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Word, Excel, PowerPoint, PDF, JPEG, TIFF
            </p>
          </div>
        )}
      </div>

      {/* Cloud Drive Pickers */}
      {showCloudPickers && !selectedFile && (
        <div className="flex gap-2">
          <button
            onClick={handleOneDrivePicker}
            disabled={cloudPickerLoading !== null}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {cloudPickerLoading === 'onedrive' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Cloud className="w-4 h-4 text-blue-500" />
            )}
            OneDrive
          </button>
          <button
            onClick={handleGoogleDrivePicker}
            disabled={cloudPickerLoading !== null}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {cloudPickerLoading === 'google_drive' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <HardDrive className="w-4 h-4 text-green-500" />
            )}
            Google Drive
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};
